import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FileText, Shield, AlertTriangle } from 'lucide-react';

const TermsOfService = () => {
  return (
    <div className="legal-page" data-testid="terms-page">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Header */}
          <div className="legal-header">
            <FileText size={48} />
            <h1>Terms of Service</h1>
            <p>Please read these terms carefully before using BlackGirlTrading-Academy</p>
            <p className="last-updated">Last updated: February 2026</p>
          </div>

          {/* Quick Links */}
          <div className="legal-nav">
            <span>Quick links:</span>
            <a href="#agreement">Agreement</a>
            <a href="#services">Services</a>
            <a href="#subscriptions">Subscriptions</a>
            <a href="#conduct">User Conduct</a>
            <a href="#liability">Liability</a>
            <a href="#governing-law">Governing Law</a>
          </div>

          {/* Important Notice */}
          <div className="legal-box warning">
            <AlertTriangle size={24} />
            <div>
              <h3>Important Notice</h3>
              <p>
                BlackGirlTrading-Academy provides <strong>educational content only</strong>. We do not provide financial advice, 
                investment recommendations, or trading signals. We are not regulated by the Financial Conduct Authority (FCA). 
                Please read our <Link to="/disclaimer">full disclaimer</Link> before using our services.
              </p>
            </div>
          </div>

          {/* Section 1 */}
          <section id="agreement" className="legal-section">
            <h2>1. Agreement to Terms</h2>
            <p>
              By accessing or using BlackGirlTrading-Academy (the "Platform"), you agree to be bound by these Terms of Service 
              ("Terms"). If you do not agree to these Terms, you must not use the Platform.
            </p>
            <p>
              These Terms constitute a legally binding agreement between you and BlackGirlTrading-Academy ("we", "us", "our"). 
              We may update these Terms from time to time, and your continued use of the Platform constitutes 
              acceptance of any changes.
            </p>
            <p>
              You must be at least 18 years old to use this Platform. By using the Platform, you confirm that 
              you are at least 18 years of age.
            </p>
          </section>

          {/* Section 2 */}
          <section id="services" className="legal-section">
            <h2>2. Description of Services</h2>
            <p>BlackGirlTrading-Academy provides:</p>
            <ul>
              <li>Educational content about financial markets and trading concepts</li>
              <li>Paper trading simulators using virtual (not real) money</li>
              <li>Market data displays for educational purposes</li>
              <li>Calculators and tools to understand trading concepts</li>
              <li>Community learning resources</li>
            </ul>
            
            <div className="legal-box critical">
              <h4>Services We Do NOT Provide:</h4>
              <ul>
                <li>Financial advice or investment recommendations</li>
                <li>Trading signals or "hot tips"</li>
                <li>Portfolio management services</li>
                <li>Brokerage or execution services</li>
                <li>Regulated financial services of any kind</li>
              </ul>
            </div>
          </section>

          {/* Section 3 */}
          <section id="subscriptions" className="legal-section">
            <h2>3. Subscriptions and Payments</h2>
            
            <h3>3.1 Free and Paid Tiers</h3>
            <p>
              We offer free and paid subscription tiers. Free accounts have limited access to features. 
              Paid subscriptions (Pro and Premium) unlock additional educational content and features.
            </p>
            
            <h3>3.2 Payment Processing</h3>
            <p>
              Payments are processed securely through Stripe. We do not store your full payment card details. 
              All prices are displayed in British Pounds (GBP) and include VAT where applicable.
            </p>
            
            <h3>3.3 Subscription Terms</h3>
            <ul>
              <li><strong>Monthly subscriptions</strong> renew automatically each month until cancelled</li>
              <li><strong>Annual subscriptions</strong> renew automatically each year until cancelled</li>
              <li><strong>Lifetime subscriptions</strong> are one-time payments with no renewal</li>
            </ul>
            
            <h3>3.4 Free Trials</h3>
            <p>
              We may offer free trial periods. You will not be charged during the trial period. 
              If you do not cancel before the trial ends, your subscription will automatically convert 
              to a paid subscription and you will be charged.
            </p>
            
            <h3>3.5 Cancellation</h3>
            <p>
              You may cancel your subscription at any time. Upon cancellation:
            </p>
            <ul>
              <li>You will retain access until the end of your current billing period</li>
              <li>No partial refunds will be given for unused time</li>
              <li>Your account will revert to the free tier after expiry</li>
            </ul>
            
            <h3>3.6 Consumer Rights</h3>
            <p>
              Under the Consumer Rights Act 2015 and Consumer Contracts Regulations 2013, you have the right 
              to cancel a subscription within 14 days of purchase for a full refund, provided you have not 
              accessed the paid content. Once you access paid content, you acknowledge that you have waived 
              your right to the 14-day cooling-off period.
            </p>
          </section>

          {/* Section 4 */}
          <section id="accounts" className="legal-section">
            <h2>4. User Accounts</h2>
            
            <h3>4.1 Account Creation</h3>
            <p>
              To access certain features, you must create an account. You agree to provide accurate, 
              current, and complete information during registration.
            </p>
            
            <h3>4.2 Account Security</h3>
            <p>
              You are responsible for maintaining the confidentiality of your account credentials. 
              You must notify us immediately of any unauthorised use of your account.
            </p>
            
            <h3>4.3 Account Termination</h3>
            <p>
              We reserve the right to suspend or terminate accounts that violate these Terms or 
              for any other reason at our discretion.
            </p>
          </section>

          {/* Section 5 */}
          <section id="conduct" className="legal-section">
            <h2>5. User Conduct</h2>
            <p>You agree not to:</p>
            <ul>
              <li>Use the Platform for any unlawful purpose</li>
              <li>Share your account credentials with others</li>
              <li>Attempt to gain unauthorised access to any part of the Platform</li>
              <li>Copy, reproduce, or redistribute our content without permission</li>
              <li>Use automated systems to access the Platform (bots, scrapers, etc.)</li>
              <li>Misrepresent our educational content as financial advice</li>
              <li>Use the Platform to promote financial products or services</li>
            </ul>
          </section>

          {/* Section 6 */}
          <section id="intellectual-property" className="legal-section">
            <h2>6. Intellectual Property</h2>
            <p>
              All content on the Platform, including text, graphics, logos, videos, and software, 
              is the property of BlackGirlTrading-Academy or its licensors and is protected by copyright, 
              trademark, and other intellectual property laws.
            </p>
            <p>
              You may not reproduce, distribute, modify, or create derivative works from our content 
              without our express written permission.
            </p>
          </section>

          {/* Section 7 */}
          <section id="liability" className="legal-section">
            <h2>7. Limitation of Liability</h2>
            
            <div className="legal-box critical">
              <h4>Important</h4>
              <p>
                Our Platform is for educational purposes only. We do not guarantee any financial outcomes. 
                Trading and investing involves substantial risk of loss.
              </p>
            </div>
            
            <h3>7.1 No Warranties</h3>
            <p>
              The Platform is provided "as is" without warranties of any kind, either express or implied. 
              We do not warrant that the Platform will be uninterrupted, error-free, or free of viruses.
            </p>
            
            <h3>7.2 Limitation</h3>
            <p>
              To the fullest extent permitted by law, BlackGirlTrading-Academy shall not be liable for any:
            </p>
            <ul>
              <li>Indirect, incidental, special, consequential, or punitive damages</li>
              <li>Loss of profits, revenue, data, or business opportunities</li>
              <li>Financial losses arising from trading or investment decisions</li>
              <li>Damages arising from your use of or inability to use the Platform</li>
            </ul>
            
            <h3>7.3 Maximum Liability</h3>
            <p>
              Our total liability to you for any claims arising from your use of the Platform shall not 
              exceed the amount you paid to us in the twelve (12) months preceding the claim, or £100, 
              whichever is greater.
            </p>
            
            <h3>7.4 Consumer Rights</h3>
            <p>
              Nothing in these Terms excludes or limits our liability for death or personal injury 
              caused by our negligence, fraud or fraudulent misrepresentation, or any other liability 
              that cannot be excluded or limited under English law.
            </p>
          </section>

          {/* Section 8 */}
          <section id="indemnification" className="legal-section">
            <h2>8. Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless BlackGirlTrading-Academy, its officers, directors, employees, 
              and agents from any claims, damages, losses, liabilities, and expenses (including legal fees) 
              arising from your use of the Platform or violation of these Terms.
            </p>
          </section>

          {/* Section 9 */}
          <section id="third-party" className="legal-section">
            <h2>9. Third-Party Services</h2>
            <p>
              Our Platform may contain links to third-party websites or services. We are not responsible 
              for the content, privacy policies, or practices of third-party sites. Market data is provided 
              by third-party sources and may be delayed or inaccurate.
            </p>
          </section>

          {/* Section 10 */}
          <section id="modifications" className="legal-section">
            <h2>10. Modifications to Service</h2>
            <p>
              We reserve the right to modify, suspend, or discontinue any part of the Platform at any time 
              without notice. We shall not be liable to you or any third party for any modification, 
              suspension, or discontinuation.
            </p>
          </section>

          {/* Section 11 */}
          <section id="governing-law" className="legal-section">
            <h2>11. Governing Law and Jurisdiction</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of England and Wales.
            </p>
            <p>
              Any disputes arising from these Terms or your use of the Platform shall be subject to the 
              exclusive jurisdiction of the courts of England and Wales.
            </p>
            <p>
              If you are a consumer, you may also be entitled to bring proceedings in your country of residence.
            </p>
          </section>

          {/* Section 12 */}
          <section id="dispute-resolution" className="legal-section">
            <h2>12. Dispute Resolution</h2>
            <p>
              If you have a complaint or dispute, please contact us first to attempt to resolve the matter 
              informally. We will endeavour to respond within 14 days.
            </p>
            <p>
              If we cannot resolve the dispute informally, you may be able to use the EU Online Dispute 
              Resolution platform at <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer">https://ec.europa.eu/consumers/odr</a>.
            </p>
          </section>

          {/* Section 13 */}
          <section id="severability" className="legal-section">
            <h2>13. Severability</h2>
            <p>
              If any provision of these Terms is found to be unenforceable or invalid, that provision 
              shall be limited or eliminated to the minimum extent necessary, and the remaining provisions 
              shall remain in full force and effect.
            </p>
          </section>

          {/* Section 14 */}
          <section id="contact" className="legal-section">
            <h2>14. Contact Information</h2>
            <p>
              If you have any questions about these Terms, please contact us at:
            </p>
            <div className="contact-box">
              <p><strong>BlackGirlTrading-Academy</strong></p>
              <p>Email: BlackGirlTradingAcademy@hotmail.com</p>
            </div>
          </section>

          {/* Footer Links */}
          <div className="legal-footer-links">
            <Link to="/privacy-policy">Privacy Policy</Link>
            <Link to="/disclaimer">Disclaimer</Link>
            <Link to="/pricing">Pricing</Link>
          </div>
        </motion.div>
      </div>

      <style>{`
        .legal-page {
          padding: 40px 0 80px;
        }

        .legal-header {
          text-align: center;
          margin-bottom: 48px;
        }

        .legal-header svg {
          color: var(--primary);
          margin-bottom: 16px;
        }

        .legal-header h1 {
          font-size: 2.5rem;
          margin-bottom: 12px;
        }

        .legal-header p {
          color: var(--text-secondary);
        }

        .last-updated {
          font-size: 14px;
          color: var(--text-muted);
          margin-top: 8px;
        }

        .legal-nav {
          display: flex;
          flex-wrap: wrap;
          gap: 16px;
          padding: 16px 24px;
          background: var(--bg-paper);
          border-radius: 12px;
          margin-bottom: 32px;
          align-items: center;
        }

        .legal-nav span {
          color: var(--text-muted);
          font-size: 14px;
        }

        .legal-nav a {
          color: var(--primary);
          text-decoration: none;
          font-size: 14px;
        }

        .legal-nav a:hover {
          text-decoration: underline;
        }

        .legal-box {
          background: var(--bg-paper);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 24px;
          margin-bottom: 32px;
        }

        .legal-box.warning {
          background: rgba(255, 204, 0, 0.05);
          border-color: rgba(255, 204, 0, 0.3);
          display: flex;
          gap: 16px;
        }

        .legal-box.warning svg {
          color: var(--warning);
          flex-shrink: 0;
          margin-top: 4px;
        }

        .legal-box.critical {
          background: rgba(255, 59, 48, 0.05);
          border-color: rgba(255, 59, 48, 0.3);
        }

        .legal-box h3, .legal-box h4 {
          margin-bottom: 12px;
        }

        .legal-box p {
          color: var(--text-secondary);
          margin: 0;
          line-height: 1.7;
        }

        .legal-box ul {
          margin: 12px 0 0 0;
          padding-left: 20px;
        }

        .legal-box li {
          color: var(--text-secondary);
          margin-bottom: 8px;
        }

        .legal-section {
          margin-bottom: 40px;
          padding-bottom: 40px;
          border-bottom: 1px solid var(--border);
        }

        .legal-section:last-of-type {
          border-bottom: none;
        }

        .legal-section h2 {
          font-size: 1.5rem;
          margin-bottom: 20px;
          color: var(--primary-light);
        }

        .legal-section h3 {
          font-size: 1.1rem;
          margin-top: 24px;
          margin-bottom: 12px;
        }

        .legal-section p {
          color: var(--text-secondary);
          line-height: 1.8;
          margin-bottom: 16px;
        }

        .legal-section ul {
          margin: 16px 0;
          padding-left: 24px;
        }

        .legal-section li {
          color: var(--text-secondary);
          margin-bottom: 8px;
          line-height: 1.6;
        }

        .legal-section a {
          color: var(--primary);
        }

        .contact-box {
          background: var(--bg-subtle);
          padding: 20px;
          border-radius: 8px;
          margin-top: 16px;
        }

        .contact-box p {
          margin: 4px 0;
        }

        .legal-footer-links {
          display: flex;
          justify-content: center;
          gap: 32px;
          padding-top: 32px;
          border-top: 1px solid var(--border);
        }

        .legal-footer-links a {
          color: var(--text-muted);
          text-decoration: none;
        }

        .legal-footer-links a:hover {
          color: var(--primary);
        }

        @media (max-width: 768px) {
          .legal-nav {
            flex-direction: column;
            align-items: flex-start;
          }

          .legal-box.warning {
            flex-direction: column;
          }
        }
      `}</style>
    </div>
  );
};

export default TermsOfService;
